import type {
  ComponentValueResponse,
  RiskAssessmentInput,
  RiskAssessmentSubmitInput,
  SaveComponentValueResponse,
  SnapshotInvalidation,
  SubmitInstanceRiskAssessmentRequest,
  SubmitInstanceRiskAssessmentResponse,
} from "@repo/shared/types";
import { get, post } from "../_client";

const BASE = "/internal/flow-instances";

export function getFlowInstanceValue(instanceId: string, goalId: string) {
  return get<ComponentValueResponse>(
    `${BASE}/${instanceId}/risk-assessment/${goalId}/flow-instance-value`,
  );
}

export function savePartial(instanceId: string, goalId: string, input: RiskAssessmentInput) {
  return post<SaveComponentValueResponse>(
    `${BASE}/${instanceId}/risk-assessment/${goalId}/partial`,
    input satisfies RiskAssessmentInput,
  );
}

export function submitRiskAssessment(
  instanceId: string,
  goalId: string,
  data: RiskAssessmentSubmitInput,
  lang?: string,
) {
  return post<SaveComponentValueResponse & { refreshHints?: SnapshotInvalidation }>(
    `${BASE}/${instanceId}/risk-assessment/${goalId}/submit`,
    { ...data, lang } satisfies RiskAssessmentSubmitInput & { lang?: string },
  );
}

// ─── Instance-level (client-level) capture — QL-212 ──────────────────────────
//
// The goal id is a PATH segment above, so these are separate URLs rather than
// the same one with a nullable parameter: a caller cannot address the wrong row
// by omitting a value.

export function getInstanceValue(instanceId: string) {
  return get<ComponentValueResponse>(`${BASE}/${instanceId}/risk-assessment/flow-instance-value`);
}

export function saveInstancePartial(instanceId: string, input: RiskAssessmentInput) {
  return post<SaveComponentValueResponse>(
    `${BASE}/${instanceId}/risk-assessment/partial`,
    input satisfies RiskAssessmentInput,
  );
}

/**
 * Submit the client-level answer. The server fans it out to every goal and
 * reports per goal what it did, so the caller knows which goal queries to
 * refresh — and which goals are still pending or unscoreable.
 */
export function submitInstanceRiskAssessment(
  instanceId: string,
  data: SubmitInstanceRiskAssessmentRequest,
  lang?: string,
) {
  return post<SubmitInstanceRiskAssessmentResponse>(
    `${BASE}/${instanceId}/risk-assessment/submit`,
    { ...data, lang } satisfies SubmitInstanceRiskAssessmentRequest & { lang?: string },
  );
}
